
import React, { useState } from "react";
import { Globe, ExternalLink, CheckCircle, Clock } from "lucide-react";
import FlexihoursLogo from "./assets/logo_3white.svg";

function App() {
  const [hoveredCountry, setHoveredCountry] = useState(null);

  // Mock flag URLs - replace with your actual flag imports
  const SpainFlag = "https://flagcdn.com/w320/es.png";
  const SouthAfricaFlag = "https://flagcdn.com/w320/za.png";
  const PortugalFlag = "https://flagcdn.com/w320/pt.png";
  const AustraliaFlag = "https://flagcdn.com/w320/au.png";
  const MoroccoFlag = "https://flagcdn.com/w320/ma.png";
  const CanadaFlag = "https://flagcdn.com/w320/ca.png";
  const UnitedStatesFlag = "https://flagcdn.com/w320/us.png";

  const countries = [
    {
      flag: SpainFlag,
      name: "España",
      redirect: "https://flexihours.es",
      status: "active",
      description: "Trabajos temporales y a tiempo parcial",
      users: "15,000+",
    },
    {
      flag: SouthAfricaFlag,
      name: "South Africa",
      redirect: "https://flexihours.co.za",
      status: "active",
      description: "Temporary casual and part-time jobs",
      users: "8,500+",
    },
    {
      flag: UnitedStatesFlag,
      name: "UnitedStates",
      redirect: "https://flexihours.us",
      status: "active",
      description: "Temporary casual and part-time jobs",
      users: "7,200+",
    },
    {
      flag: PortugalFlag,
      name: "Portugal",
      redirect: "https://flexihours.pt",
      status: "inactive",
      description: "Empregos temporários a tempo parcial",
      users: "8,500+",
    },
    {
      flag: AustraliaFlag,
      name: "Australia",
      redirect: "https://flexihours.com.au",
      status: "inactive",
      description: "Temporary casual and part-time jobs",
      users: "5,000+",
    },
    {
      flag: CanadaFlag,
      name: "Canada",
      redirect: "https://flexihours.ca",
      status: "inactive",
      description: "Temporary casual and part-time jobs",
      users: "7,200+",
    },

    {
      flag: MoroccoFlag,
      name: "Morocco",
      redirect: "https://flexihours.ma",
      status: "inactive",
      description: "Travail temporaire et à temps partiel",
      users: "3,800+",
    },
  ];
  const handleCountryClick = (country: any) => {
    if (country.status === "active") {
      window.open(country.redirect, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      {/* <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>
      
      <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div> */}
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

      {/* Main Content */}
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Header Section */}
        <div className="mb-16">
          <div className="flex items-center justify-center gap-4 mb-8">
            {/* <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
              <Clock className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
              FlexiHours
            </h1> */}
            <img
              src={FlexihoursLogo}
              className="h-20 w-40"
              alt="flexihours-logo"
            />
          </div>

          <p className="text-xl md:text-2xl text-white/90 mb-4 font-light">
            Choose your region to get started
          </p>

          <div className="flex items-center justify-center gap-2 text-white/70">
            <Globe className="w-5 h-5" />
            <span className="text-sm">Available in multiple countries</span>
          </div>
        </div>

        {/* Country Selection */}
        <div className="space-y-4 mb-12">
          {countries.map((country, index: any) => (
            <div
              key={country.name}
              className={`group relative overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-105 ${country.status === "active"
                ? "cursor-pointer hover:shadow-2xl"
                : "cursor-not-allowed opacity-60"
                }`}
              onMouseEnter={() => setHoveredCountry(index)}
              onMouseLeave={() => setHoveredCountry(null)}
              onClick={() => handleCountryClick(country)}
            >
              <div
                className={`absolute inset-0 transition-all duration-300 ${country.status === "active"
                  ? "bg-gradient-to-r from-white/10 to-white/5 group-hover:from-white/20 group-hover:to-white/10"
                  : "bg-gradient-to-r from-gray-500/20 to-gray-500/10"
                  } backdrop-blur-sm border border-white/20`}
              ></div>

              <div className="relative z-10 flex items-center justify-between p-6">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <img
                      src={country.flag}
                      className="w-16 h-12 object-cover rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-110"
                      alt={`${country.name} flag`}
                    />
                    {country.status === "active" && (
                      <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>

                  <div className="text-left">
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {country.name}
                    </h3>
                    <p className="text-white/70 text-sm mb-2">
                      {country.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-white/60">
                      {/* <span>{country.users} users</span> */}
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${country.status === "active"
                          ? "bg-green-500/20 text-green-300"
                          : "bg-gray-500/20 text-gray-300"
                          }`}
                      >
                        {country.status === "active" ? "Active" : "Coming Soon"}
                      </span>
                    </div>
                  </div>
                </div>

                {country.status === "active" && (
                  <div className="flex items-center gap-2 text-white/70 group-hover:text-white transition-colors duration-300">
                    <span className="text-sm font-medium">Visit Site</span>
                    <ExternalLink className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                )}
              </div>

              {/* Hover Effect */}
              {hoveredCountry === index && country.status === "active" && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 animate-pulse"></div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Section */}
        <div className="text-center text-white/60 ">
          <p className="mb-4">
            FlexiHours is expanding globally. More regions coming soon.
          </p>

          <div className="flex justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Active</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span>Coming Soon</span>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full animate-ping"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
